import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notificationService } from "../../services/api/notification.service";

// Async thunks for notification operations

// Get all notifications with pagination
export const fetchNotifications = createAsyncThunk(
  "notification/fetchNotifications",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await notificationService.getNotifications(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

// Get notification by ID
export const fetchNotificationById = createAsyncThunk(
  "notification/fetchNotificationById",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await notificationService.getNotificationById(notificationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notification"
      );
    }
  }
);

// Create new notification
export const createNotification = createAsyncThunk(
  "notification/createNotification",
  async (notificationData, { rejectWithValue }) => {
    try {
      const response = await notificationService.createNotification(notificationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create notification"
      );
    }
  }
);

// Update notification
export const updateNotification = createAsyncThunk(
  "notification/updateNotification",
  async ({ notificationId, notificationData }, { rejectWithValue }) => {
    try {
      const response = await notificationService.updateNotification(
        notificationId,
        notificationData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update notification"
      );
    }
  }
);

// Delete notification
export const deleteNotification = createAsyncThunk(
  "notification/deleteNotification",
  async (notificationId, { rejectWithValue }) => {
    try {
      await notificationService.deleteNotification(notificationId);
      return notificationId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete notification"
      );
    }
  }
);

// Get notification types
export const fetchNotificationTypes = createAsyncThunk(
  "notification/fetchNotificationTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.getNotificationTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notification types"
      );
    }
  }
);

// Get active notifications by role
export const fetchActiveNotificationsByRole = createAsyncThunk(
  "notification/fetchActiveNotificationsByRole",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await notificationService.getActiveNotificationsByRole(roleId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch active notifications"
      );
    }
  }
);

// Deactivate notification
export const deactivateNotification = createAsyncThunk(
  "notification/deactivateNotification",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await notificationService.deactivateNotification(notificationId);
      return { notificationId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to deactivate notification"
      );
    }
  }
);

// Activate notification
export const activateNotification = createAsyncThunk(
  "notification/activateNotification",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await notificationService.activateNotification(notificationId);
      return { notificationId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to activate notification"
      );
    }
  }
);

// Upload document temporarily
export const uploadDocumentTemp = createAsyncThunk(
  "notification/uploadDocumentTemp",
  async (file, { rejectWithValue }) => {
    try {
      const response = await notificationService.uploadDocumentTemp(file);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload document"
      );
    }
  }
);

// Initial state
const initialState = {
  notifications: [],
  notificationTypes: [],
  activeNotifications: [],
  currentNotification: null,
  uploadedFilePath: null,
  isUploading: false,
  isLoading: false,
  error: null,
  totalCount: 0,
};

// Notification slice
const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentNotification: (state, action) => {
      state.currentNotification = action.payload;
    },
    clearCurrentNotification: (state) => {
      state.currentNotification = null;
    },
    clearUploadedFilePath: (state) => {
      state.uploadedFilePath = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch notification by ID
      .addCase(fetchNotificationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotificationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentNotification = action.payload.data || null;
      })
      .addCase(fetchNotificationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create notification
      .addCase(createNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications.push(action.payload.data);
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Update notification
      .addCase(updateNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = state.notifications.map((notification) =>
          notification.notification_id === action.payload.data.notification_id
            ? action.payload.data
            : notification
        );
        state.currentNotification = action.payload.data;
      })
      .addCase(updateNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete notification
      .addCase(deleteNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = state.notifications.filter(
          (notification) => notification.notification_id !== action.payload
        );
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch notification types
      .addCase(fetchNotificationTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotificationTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notificationTypes = action.payload.data || [];
      })
      .addCase(fetchNotificationTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch active notifications by role
      .addCase(fetchActiveNotificationsByRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveNotificationsByRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeNotifications = action.payload.data || [];
      })
      .addCase(fetchActiveNotificationsByRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Deactivate notification
      .addCase(deactivateNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deactivateNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = state.notifications.map((notification) =>
          notification.notification_id === action.payload.notificationId
            ? { ...notification, notification_status: "Inactive" }
            : notification
        );
      })
      .addCase(deactivateNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Activate notification
      .addCase(activateNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(activateNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = state.notifications.map((notification) =>
          notification.notification_id === action.payload.notificationId
            ? { ...notification, notification_status: "Active" }
            : notification
        );
      })
      .addCase(activateNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Upload document temporarily
      .addCase(uploadDocumentTemp.pending, (state) => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(uploadDocumentTemp.fulfilled, (state, action) => {
        state.isUploading = false;
        state.uploadedFilePath = action.payload.data?.[0]?.file_path || null;
      })
      .addCase(uploadDocumentTemp.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentNotification, clearCurrentNotification, clearUploadedFilePath } =
  notificationSlice.actions;
export default notificationSlice.reducer;
